import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Star } from 'lucide-react';
import { motion, useScroll, useTransform, useMotionValueEvent } from 'framer-motion';
import { trackEvent } from '../../lib/track';

/**
 * HammerHero — landing-page hero for absolutehammers.com.
 *
 * Static cinematic composite backdrop with framer-motion scroll parallax,
 * a masked line-by-line headline reveal on load, and a scroll-driven
 * cue-swap headline. Smooth momentum scrolling is provided by Lenis in App.js.
 */
const EASE = [0.22, 1, 0.36, 1];

const maskLine = {
  hidden: { y: '115%' },
  visible: (i) => ({
    y: '0%',
    transition: { duration: 0.9, ease: EASE, delay: 0.45 + i * 0.14 },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (d) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: EASE, delay: d },
  }),
};

const HEADLINES = [
  ['Pneumatic Power,', 'Evolved.'],
  ['Hits harder,', 'and smarter.'],
];

// Owner-set stock figure for the hero counter. One-line update when a unit sells.
const STOCK_COUNT = 2;

// Rugged "Proudly made in the USA" rubber-stamp badge — grey duotone asset
// derived from the classic stamp artwork, slam entrance, slight diagonal.
function UsaStamp() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 1.8, rotate: 28 }}
      animate={{ opacity: 1, scale: 1, rotate: 12 }}
      transition={{ delay: 1.2, type: 'spring', stiffness: 320, damping: 16 }}
      className="absolute top-[30vh] right-4 sm:top-24 sm:right-8 lg:right-12 z-20 w-20 sm:w-24 md:w-28 pointer-events-none"
      data-testid="hero-usa-stamp"
      aria-hidden="true"
    >
      <img
        src="/assets/usa-stamp.webp"
        alt=""
        className="w-full h-auto block opacity-90"
      />
    </motion.div>
  );
}

// Live Google rating pill — only renders when /api/reviews is in live
// (source=google) mode, so we never display a rating we didn't verify.
// Links out to the actual Google review panel for the business.
function GoogleRatingBadge() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d && d.source === 'google' && d.rating) {
          setData({ rating: d.rating, count: d.userRatingCount });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;

  return (
    <motion.a
      href="https://search.google.com/local/reviews?placeid=ChIJFT4kXA9r24gRP8xNworXlgA"
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: EASE }}
      data-testid="hero-google-rating-badge"
      aria-label={`Rated ${data.rating} out of 5 on Google from ${data.count} reviews`}
      className="inline-flex items-center justify-center gap-2.5 border border-white/10 bg-black/50 backdrop-blur-sm rounded-md px-4 min-h-[42px] sm:h-[46px] hover:border-cyan-300/40 transition-colors"
    >
      <span className="flex items-center gap-0.5" aria-hidden="true">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className="w-3.5 h-3.5 fill-cyan-300 text-cyan-300 [filter:drop-shadow(0_0_3px_rgba(255,255,255,0.7))]"
          />
        ))}
      </span>
      <span className="font-tech text-white/90 text-xs sm:text-sm uppercase tracking-[0.16em] whitespace-nowrap">
        {data.rating.toFixed(1)} on Google
      </span>
      <span className="font-tech text-zinc-500 text-[10px] sm:text-xs uppercase tracking-[0.16em] whitespace-nowrap">
        · {data.count} reviews
      </span>
    </motion.a>
  );
}

export default function HammerHero() {
  const wrapRef = useRef(null);
  const [cueIndex, setCueIndex] = useState(0);

  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ['start start', 'end end'],
  });
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.08]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -24]);

  useMotionValueEvent(scrollYProgress, 'change', (p) => {
    const next = p < 0.5 ? 0 : 1;
    setCueIndex((prev) => (prev === next ? prev : next));
  });

  return (
    <section
      ref={wrapRef}
      id="hammer-top"
      data-testid="hammer-hero"
      className="relative w-full"
      style={{ height: '180vh' }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-black">
        {/* Hero backdrop — cinematic composite with graceful CSS fallback.
            framer-motion scroll parallax on the outer div; inner img runs a
            slow Ken Burns idle animation so the still feels alive. */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute inset-0"
            style={{ scale: bgScale, y: bgY, willChange: 'transform' }}
          >
            <img
              src="/assets/hammer_hero.jpg?v=17"
              alt=""
              aria-hidden="true"
              className="hammer-hero-kenburns absolute inset-0 w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </motion.div>
          {/* CSS fallback / atmospheric enhancer, sits behind the image but shows through if img errors */}
          <div
            className="absolute inset-0 -z-10"
            style={{
              background:
                'radial-gradient(70% 55% at 62% 55%, rgba(45,212,212,0.35) 0%, rgba(45,212,212,0.06) 42%, rgba(0,0,0,0) 75%), #000',
            }}
          />
        </div>

        {/* Vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(80% 70% at 55% 55%, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0.9) 100%)',
          }}
        />
        <div className="absolute inset-0 pointer-events-none teal-fog opacity-45" />

        <UsaStamp />

        {/* Foreground content — masked line-by-line reveal on load */}
        <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 flex flex-col justify-between pt-[14vh] sm:pt-[16vh] pb-[10vh] sm:pb-[12vh] md:pb-[14vh]">
          <div className="overflow-hidden">
            <motion.div
              initial={{ y: '112%' }}
              animate={{ y: '0%' }}
              transition={{ duration: 1, ease: EASE, delay: 0.15 }}
              className="ghost-text-tight whitespace-nowrap text-[10vw] sm:text-[9vw] md:text-[8vw] lg:text-[7vw] xl:text-[6.5vw] 2xl:text-[125px] leading-[0.85] text-left"
            >
              ABSOLUTE HAMMERS
            </motion.div>
          </div>

          <div>
            <h1
              key={cueIndex}
              aria-label={HEADLINES[cueIndex].join(' ')}
              className="font-display text-white font-bold uppercase leading-[0.95] tracking-tight text-[8vw] sm:text-[7vw] md:text-[5.5vw] lg:text-[4.8vw] max-w-5xl"
            >
              {HEADLINES[cueIndex].map((line, i) => (
                <span key={line} className="block overflow-hidden">
                  <motion.span
                    className="block"
                    custom={i}
                    variants={maskLine}
                    initial="hidden"
                    animate="visible"
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              key={`footnote-${cueIndex}`}
              variants={fadeUp}
              custom={0.55}
              initial="hidden"
              animate="visible"
              className="mt-4 text-zinc-500 text-[10px] sm:text-xs md:text-sm tracking-wide"
            >
              <span className="block">Pneumatic pile driving hammers for sale — $10,500 each.</span>
              <span className="block mt-0.5">Proudly built in Fort Myers, FL, currently shipping nationwide.</span>
              <span className="block mt-0.5 text-cyan-300/90" data-testid="hero-stock-counter">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-300 animate-pulse mr-1.5 align-middle" />
                In stock — {STOCK_COUNT} units this run, ready to ship.
              </span>
            </motion.p>

            <motion.div
              variants={fadeUp}
              custom={0.95}
              initial="hidden"
              animate="visible"
              className="mt-6 sm:mt-8 flex flex-wrap items-center gap-3 sm:gap-4"
            >
              <a
                href="#hammer-quote"
                onClick={() => trackEvent('buy_click', { location: 'hero' })}
                data-testid="hammer-hero-buy-btn"
                className="btn-teal inline-flex items-center justify-center text-center w-full min-h-[42px] sm:w-[224px] sm:h-[46px] border border-transparent px-5 sm:px-6 py-3 rounded-md text-xs sm:text-sm uppercase tracking-[0.16em] sm:tracking-[0.18em] whitespace-nowrap"
              >
                Buy Now&nbsp;·&nbsp;$10,500*
              </a>
              <a
                href="#hammer-specs"
                data-testid="hammer-hero-specs-btn"
                className="btn-ghost inline-flex items-center justify-center text-center w-full min-h-[42px] sm:w-[224px] sm:h-[46px] px-5 sm:px-6 py-3 rounded-md text-xs sm:text-sm uppercase tracking-[0.16em] sm:tracking-[0.18em] whitespace-nowrap"
              >
                Features & Specs
              </a>
              <GoogleRatingBadge />
            </motion.div>
          </div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 text-zinc-400 text-[10px] sm:text-[11px] uppercase tracking-[0.3em]"
        >
          <span>Scroll</span>
          <ChevronDown className="w-4 h-4 animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
}
